<template lang="pug">
.form-page
    .header
        IQToolMiddlePaneHeader(:header="$t(`${baseTranslationPath}.name`)"
           :title="formTitle"
           @goToListPage="navigateAway()")
        .button-container
              aiq-button(size="small" class="btn" @click="saveForm(true)") {{ $t(`${baseTranslationPath}.save_as_draft`) }}
              aiq-tooltip(:disabled="canGenerateEntities" :content="entitiesDisabledReason" placement="bottom" :show-after="300")
                span.btn-wrap
                  aiq-button(size="small" class="btn" :disabled="!canGenerateEntities" :loading="busy.entities" @click="onGenerateEntities") {{ $t(`${baseTranslationPath}.generate_entities`) }}
              aiq-button(v-if="isDraft" type="primary" size="small" class="btn" :loading="busy.publish" @click="onPublish") {{ $t(`${baseTranslationPath}.publish`) }}
              aiq-tooltip(v-else :disabled="canCreateArtifacts" :content="artifactsDisabledReason" placement="bottom" :show-after="300")
                span.btn-wrap
                  aiq-button(type="primary" size="small" class="btn" :disabled="!canCreateArtifacts" :loading="busy.artifacts" @click="onCreateArtifacts") {{ $t(`${baseTranslationPath}.create_artifacts`) }}
              aiq-dropdown(trigger="click" popper-class="forms-kebab-dropdown" @command="onMenuCommand")
                aiq-button.btn.btn-kebab(size="small")
                  fa-icon(icon="fas fa-ellipsis-vertical")
                template(#dropdown)
                  aiq-dropdown-menu
                    aiq-dropdown-item.dropdown_button(command="delete-artifacts" :disabled="isDraft") {{ $t(`${baseTranslationPath}.delete_artifacts`) }}
                    aiq-dropdown-item.dropdown_button.delete-form-item(command="delete-form" divided) {{ $t(`${baseTranslationPath}.delete_form`) }}
    .workspace(v-loading="loading")
        //- LEFT: questions (draggable, ordered) + fields list.
        .left-pane
            QuestionsPane(
                :questions="questions"
                :fields="fields"
                :groups="groups"
                :selectedQuestionId="selectedQuestion && selectedQuestion.id"
                :selectedFieldId="selectedField && selectedField.id"
                :selectedGroupId="selectedGroup && selectedGroup.id"
                @select-question="onSelectQuestion"
                @select-field="onSelectField"
                @select-group="onSelectGroup"
                @reorder-question="onReorderQuestion"
                @add-question="openAddQuestion"
                @add-group="openAddGroup"
                @open-settings="onOpenQuestionSettings"
                @open-field-settings="onOpenFieldSettings"
                @open-group-settings="onOpenGroupSettings"
            )
        //- RIGHT: 3 tabbed windows.
        .right-pane
            aiq-tabs.right-tabs(v-model="activeTab" type="border-card")
                aiq-tab-pane(:label="selectedGroup ? $t(`${baseTranslationPath}.tab_group_details`) : $t(`${baseTranslationPath}.tab_mapped_fields`)" name="mapped_fields")
                    GroupDetails(
                        v-if="selectedGroup"
                        :group="selectedGroup"
                        :fields="selectedGroupFields"
                        :questions="selectedGroupQuestions"
                        :selectedFieldId="selectedField && selectedField.id"
                        @select-field="onSelectGroupField"
                        @select-question="onSelectQuestion"
                        @open-field-settings="onOpenGroupFieldSettings"
                        @open-settings="onOpenGroupSettings(selectedGroup)"
                    )
                    MappedFields(
                        v-else
                        :question="selectedQuestion"
                        :fields="fields"
                        :selectedFieldId="selectedField && selectedField.id"
                        @select-field="onSelectField"
                        @open-field-settings="onOpenFieldSettings"
                        @open-settings="showQuestionSettings = true"
                    )
                aiq-tab-pane(v-if="!selectedGroup" :label="$t(`${baseTranslationPath}.tab_mapped_entities`)" name="mapped_entities")
                    Selection(
                        :selectedField="selectedField"
                        :formId="formId"
                        :fieldIndex="selectedFieldIndex"
                        :fields="fields"
                        @updated="reloadFields"
                    )
                aiq-tab-pane(:label="$t(`${baseTranslationPath}.tab_pdf`)" name="pdf")
                    PdfFieldEditor(
                        v-if="activeTab === 'pdf' && fileId"
                        :fileId="fileId"
                        :formId="formId"
                        :fields="fields"
                        :highlightNames="pdfHighlightNames"
                        :selectedQuestionKey="selectedQuestion && selectedQuestion.question_key"
                        @field-added="onFieldAdded"
                        @field-updated="onFieldUpdated"
                        @edit-field="onOpenFieldSettings"
                    )
    AddQuestionDialog(
        v-model="showAddQuestion"
        :formId="formId"
        :fields="unlinkedFields"
        @created="onQuestionCreated"
    )
    QuestionSettingsDialog(
        v-model="showQuestionSettings"
        :formId="formId"
        :question="selectedQuestion"
        :fields="fields"
        @updated="onQuestionUpdated"
        @deleted="onQuestionDeleted"
    )
    FieldSettingsDialog(
        v-model="showFieldSettings"
        :formId="formId"
        :field="selectedField"
        :questions="questions"
        @updated="onFieldUpdated"
        @deleted="onFieldDeleted"
    )
    GroupSettingsDialog(
        v-model="showGroupSettings"
        :formId="formId"
        :group="editingGroup"
        :fields="fields"
        @saved="onGroupSaved"
        @deleted="onGroupDeleted"
    )
</template>
<script>
import IQToolMiddlePaneHeader from '@/pages/IQTools/Partials/IQToolMiddlePaneHeader/IQToolMiddlePaneHeader.vue';
import get from 'lodash/get';
import QuestionsPane from './QuestionsPane.vue';
import MappedFields from './MappedFields.vue';
import Selection from './Selection.vue';
import PdfFieldEditor from './PdfFieldEditor.vue';
import AddQuestionDialog from './AddQuestionDialog.vue';
import QuestionSettingsDialog from './QuestionSettingsDialog.vue';
import FieldSettingsDialog from './FieldSettingsDialog.vue';
import GroupDetails from './GroupDetails.vue';
import GroupSettingsDialog from './GroupSettingsDialog.vue';
import { STATUS } from './fieldUtil';

export default {
  name: 'FormPage',
  components: {
    IQToolMiddlePaneHeader,
    QuestionsPane,
    MappedFields,
    Selection,
    PdfFieldEditor,
    AddQuestionDialog,
    QuestionSettingsDialog,
    FieldSettingsDialog,
    GroupDetails,
    GroupSettingsDialog,
  },
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
      formDetails: null,
      questions: [],
      fields: [],
      groups: [],
      selectedQuestion: null,
      selectedField: null,
      selectedGroup: null,
      activeTab: 'mapped_fields',
      showAddQuestion: false,
      showQuestionSettings: false,
      showFieldSettings: false,
      showGroupSettings: false,
      editingGroup: null,
      loading: false,
      busy: {
        publish: false,
        entities: false,
        artifacts: false,
      },
    };
  },
  computed: {
    formId() {
      return get(this.formDetails, 'id', null);
    },
    // base-api file id used to fetch the source PDF (/files/:id).
    fileId() {
      return get(this.formDetails, 'external_file_id', null);
    },
    // fields connected to the selected question, used to highlight PDF boxes.
    selectedQuestionFieldNames() {
      if (!this.selectedQuestion) {
        return [];
      }
      // Emit BOTH field_name and field_key for every mapped field: native PDF
      // boxes may be keyed by either, so collapsing to one would leave boxes
      // named by the other identifier un-highlighted.
      return this.fields
        .filter(f => get(f, 'question_ref') === this.selectedQuestion.id)
        .flatMap(f => [get(f, 'field_name'), get(f, 'field_key')])
        .filter(Boolean);
    },
    // fields that belong to the selected group (group_ref === group.id).
    selectedGroupFields() {
      if (!this.selectedGroup) {
        return [];
      }
      return this.fields.filter(f => get(f, 'group_ref') === this.selectedGroup.id);
    },
    // questions whose fields fall in the selected group (derived, backend sends
    // question_keys on the group row).
    selectedGroupQuestions() {
      if (!this.selectedGroup) {
        return [];
      }
      const keys = new Set(get(this.selectedGroup, 'question_keys', []) || []);
      return this.questions.filter(q => keys.has(get(q, 'question_key')));
    },
    // Names fed to the PDF editor to highlight boxes: the selected question's
    // fields plus the individually selected field. Each field contributes both
    // its field_name and field_key, since native boxes may be keyed by either.
    pdfHighlightNames() {
      const names = [...this.selectedQuestionFieldNames];
      if (this.selectedGroup) {
        this.selectedGroupFields.forEach((f) => {
          names.push(get(f, 'field_name'), get(f, 'field_key'));
        });
      }
      if (this.selectedField) {
        names.push(get(this.selectedField, 'field_name'), get(this.selectedField, 'field_key'));
      }
      return names.filter(Boolean);
    },
    // fields not yet part of any question, offered when creating a new question.
    unlinkedFields() {
      return this.fields.filter(f => !get(f, 'question_ref'));
    },
    // 1-based position of the selected field within the form's field list.
    selectedFieldIndex() {
      if (!this.selectedField) {
        return null;
      }
      const index = this.fields.findIndex(f => f.id === this.selectedField.id);
      return index === -1 ? null : index + 1;
    },
    isDraft() {
      return get(this.formDetails, 'draft', true) !== false;
    },
    // Breadcrumb shows "Forms > <form title>"; fall back to the generic label
    // until the form details load.
    formTitle() {
      return get(this.formDetails, 'title') || this.$t(`${this.baseTranslationPath}.manage`);
    },
    extractCompleted() {
      return get(this.formDetails, 'extract_status') === STATUS.completed;
    },
    matchCompleted() {
      return get(this.formDetails, 'match_status') === STATUS.completed;
    },
    // Entities are generated from questions; the extract must be done AND the
    // form must be published (backend rejects create_entities on a draft).
    canGenerateEntities() {
      return this.extractCompleted && !this.isDraft && !this.busy.entities;
    },
    entitiesDisabledReason() {
      if (this.isDraft) {
        return this.$t(`${this.baseTranslationPath}.entities_disabled_draft`);
      }
      return this.$t(`${this.baseTranslationPath}.entities_disabled_extract`);
    },
    // Artifacts compile the published form; extract+match must be done and the
    // form must no longer be a draft.
    canCreateArtifacts() {
      return this.extractCompleted && this.matchCompleted && !this.isDraft && !this.busy.artifacts;
    },
    artifactsDisabledReason() {
      if (this.isDraft) {
        return this.$t(`${this.baseTranslationPath}.artifacts_disabled_draft`);
      }
      return this.$t(`${this.baseTranslationPath}.artifacts_disabled_ready`);
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        const id = get(this, '$route.params.id');
        const formResp = await this.$store.dispatch('agentForms/getForm', { id });
        this.formDetails = get(formResp, 'data', null);
        if (!this.formDetails || !get(this.formDetails, 'id')) {
          this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.not_found`));
          this.navigateAway();
          return;
        }
        const match_status = get(this.formDetails, 'match_status');
        if (match_status !== STATUS.completed) {
          this.$aiq.notify.info(this.$t(`${this.baseTranslationPath}.navigate_error`));
          this.navigateAway();
          return;
        }
        await Promise.all([this.reloadQuestions(), this.reloadFields(), this.reloadGroups()]);
        if (this.questions.length) {
          this.onSelectQuestion(this.questions[0]);
        }
      } catch (e) {
        // Unknown / deleted form id (404) or any load failure: don't leave an
        // empty editor shell on screen — notify and go back to the list.
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.not_found`));
        this.navigateAway();
      } finally {
        this.loading = false;
      }
    },
    async reloadQuestions() {
      const resp = await this.$store.dispatch('agentForms/listFormQuestions', { formId: this.formId });
      this.questions = get(resp, 'data', []);
      // keep the selected question reference pointing at the refreshed row.
      if (this.selectedQuestion) {
        this.selectedQuestion = this.questions.find(q => q.id === this.selectedQuestion.id) || null;
      }
    },
    async reloadFields() {
      const resp = await this.$store.dispatch('agentForms/listFormFields', { formId: this.formId });
      this.fields = get(resp, 'data', []);
      if (this.selectedField) {
        this.selectedField = this.fields.find(f => f.id === this.selectedField.id) || null;
      }
    },
    async reloadGroups() {
      const resp = await this.$store.dispatch('agentForms/listFormGroups', { formId: this.formId });
      this.groups = get(resp, 'data', []);
      if (this.selectedGroup) {
        this.selectedGroup = this.groups.find(g => g.id === this.selectedGroup.id) || null;
      }
    },
    onSelectQuestion(question) {
      this.selectedGroup = null;
      this.selectedQuestion = question;
      // default-select the question's first connected field for the entities tab.
      const first = this.fields
        .filter(f => get(f, 'question_ref') === get(question, 'id'))
        .sort((a, b) => get(a, 'question_position', 0) - get(b, 'question_position', 0))[0];
      this.selectedField = first || null;
      if (this.activeTab === 'mapped_entities' && !first) {
        this.activeTab = 'mapped_fields';
      }
    },
    // Selecting a field from inside a group's member list must not drop the group
    // selection, or the pane swaps from GroupDetails to MappedFields under the
    // user. Only a field picked outside that context clears it.
    onSelectField(field, { keepGroup = false } = {}) {
      if (!keepGroup) {
        this.selectedGroup = null;
      }
      this.selectedField = field;
    },
    onSelectGroupField(field) {
      this.onSelectField(field, { keepGroup: true });
    },
    // Selecting a group is a distinct mode: it clears the question/field
    // selection, hides the Mapped Entities tab (a group isn't entity-linked) and
    // shows the group's member fields + questions in the Mapped Fields tab.
    onSelectGroup(group) {
      this.selectedGroup = group;
      this.selectedQuestion = null;
      this.selectedField = null;
      if (this.activeTab === 'mapped_entities') {
        this.activeTab = 'mapped_fields';
      }
    },
    async onReorderQuestion({ question, newIndex }) {
      try {
        await this.$store.dispatch('agentForms/updateFormQuestion', {
          formId: this.formId,
          questionId: question.id,
          position: newIndex,
        });
        await this.reloadQuestions();
      } catch (_) {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.reorder_error`));
        await this.reloadQuestions();
      }
    },
    openAddQuestion() {
      this.showAddQuestion = true;
    },
    async onQuestionCreated(created) {
      await Promise.all([this.reloadQuestions(), this.reloadFields()]);
      const match = this.questions.find(q => q.id === get(created, 'id'))
        || this.questions.find(q => q.question_key === get(created, 'question_key'));
      if (match) {
        this.onSelectQuestion(match);
      }
    },
    async onFieldAdded() {
      await this.reloadFields();
    },
    onOpenQuestionSettings(question) {
      this.onSelectQuestion(question);
      this.showQuestionSettings = true;
    },
    async onQuestionUpdated(questionId) {
      // target_field_ids edits can move fields between questions, so refresh both.
      await Promise.all([this.reloadQuestions(), this.reloadFields()]);
      const match = this.questions.find(q => q.id === questionId);
      if (match) {
        this.onSelectQuestion(match);
      }
    },
    async onQuestionDeleted() {
      this.selectedQuestion = null;
      this.selectedField = null;
      await Promise.all([this.reloadQuestions(), this.reloadFields()]);
      if (this.questions.length) {
        this.onSelectQuestion(this.questions[0]);
      }
    },
    onOpenFieldSettings(field) {
      this.onSelectField(field);
      this.showFieldSettings = true;
    },
    onOpenGroupFieldSettings(field) {
      this.onSelectGroupField(field);
      this.showFieldSettings = true;
    },
    async onFieldUpdated() {
      // field_type / question link edits can re-project rows, so refresh both.
      await Promise.all([this.reloadQuestions(), this.reloadFields()]);
    },
    async onFieldDeleted() {
      this.selectedField = null;
      await Promise.all([this.reloadQuestions(), this.reloadFields()]);
    },
    openAddGroup() {
      this.editingGroup = null;
      this.showGroupSettings = true;
    },
    onOpenGroupSettings(group) {
      this.onSelectGroup(group);
      this.editingGroup = group;
      this.showGroupSettings = true;
    },
    // Group membership edits re-project form_fields.group_ref and re-derive each
    // group's question_keys, so refresh fields + groups (and questions, whose
    // group association is derived from fields).
    async onGroupSaved(saved) {
      await Promise.all([this.reloadQuestions(), this.reloadFields(), this.reloadGroups()]);
      const match = this.groups.find(g => g.id === get(saved, 'id'))
        || this.groups.find(g => g.group_key === get(saved, 'group_key'));
      if (match) {
        this.onSelectGroup(match);
        // reloadGroups replaced the row objects; repoint the open dialog at the
        // fresh one so flags it reads (is_question) are not a stale snapshot.
        if (this.editingGroup) {
          this.editingGroup = match;
        }
      }
    },
    async onGroupDeleted() {
      this.selectedGroup = null;
      await Promise.all([this.reloadQuestions(), this.reloadFields(), this.reloadGroups()]);
    },
    navigateAway() {
      const id = get(this, '$route.params.id');
      if (id && this.$route.matched.length > 1) {
        const currentPath = this.$route.path;
        const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
        const name = get(this.$route, 'meta.parentName');
        this.$router.push(name ? { name } : parentPath);
      }
    },
    async saveForm(value = false) {
      const id = get(this, '$route.params.id');
      const payload = {
        id,
        body: {
          title: this.formDetails.title,
          draft: value,
        },
      };
      try {
        await this.$store.dispatch('agentForms/updateForm', payload);
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.form_saved`));
        this.navigateAway();
      } catch (_) {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.form_save_error`));
      }
    },
    // Publish flips draft=false and keeps the user on the page so the primary
    // action morphs into "Create Artifacts".
    async onPublish() {
      this.busy.publish = true;
      try {
        await this.$store.dispatch('agentForms/updateForm', {
          id: this.formId,
          body: { title: this.formDetails.title, draft: false },
        });
        this.formDetails = { ...this.formDetails, draft: false };
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.form_published`));
      } catch (e) {
        this.$aiq.notify.error(this.errorMessage(e, 'form_publish_error'));
      } finally {
        this.busy.publish = false;
      }
    },
    async onGenerateEntities() {
      this.busy.entities = true;
      try {
        await this.$store.dispatch('agentForms/createFormEntities', { id: this.formId });
        await this.refreshData();
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.entities_created`));
      } catch (e) {
        this.$aiq.notify.error(this.errorMessage(e, 'entities_error'));
      } finally {
        this.busy.entities = false;
      }
    },
    // The artifact compiler rejects any question whose answer_type is missing
    // (backend fails with "Unsupported question type: undefined"). Surface the
    // offending question(s) up front so the user knows exactly what to fix.
    invalidAnswerTypeQuestions() {
      return this.questions.filter(q => !get(q, 'answer_type'));
    },
    questionLabel(q) {
      return get(q, 'prompt') || get(q, 'question_key') || `#${get(q, 'id', '?')}`;
    },
    async onCreateArtifacts() {
      const invalid = this.invalidAnswerTypeQuestions();
      if (invalid.length) {
        const names = invalid.map(q => this.questionLabel(q)).join(', ');
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.artifacts_invalid_answer_type`, { questions: names }),
        );
        return;
      }
      this.busy.artifacts = true;
      try {
        await this.$store.dispatch('agentForms/compileFormWorkflow', { id: this.formId });
        await this.refreshData();
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.artifacts_created`));
      } catch (e) {
        this.$aiq.notify.error(this.errorMessage(e, 'artifacts_error'));
      } finally {
        this.busy.artifacts = false;
      }
    },
    async onMenuCommand(command) {
      if (command === 'delete-artifacts') {
        await this.onDeleteArtifacts();
      } else if (command === 'delete-form') {
        await this.onDeleteForm();
      }
    },
    async onDeleteForm() {
      const ok = await this.$aiq.confirm(
        this.$t(`${this.baseTranslationPath}.delete_confirm_title`),
        this.$t(`${this.baseTranslationPath}.delete_confirm_content`, { name: get(this.formDetails, 'title', '') }),
      ).catch(() => false);
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch('agentForms/deleteForm', { id: this.formId });
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.deleted`));
        this.navigateAway();
      } catch (e) {
        this.$aiq.notify.error(this.errorMessage(e, 'delete_error'));
      }
    },
    async onDeleteArtifacts() {
      const ok = await this.$aiq.confirm(
        this.$t(`${this.baseTranslationPath}.delete_artifacts_title`),
        this.$t(`${this.baseTranslationPath}.delete_artifacts_confirm`),
      ).catch(() => false);
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch('agentForms/deleteFormArtifacts', { id: this.formId });
        await this.refreshData();
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.artifacts_deleted`));
      } catch (e) {
        this.$aiq.notify.error(this.errorMessage(e, 'artifacts_delete_error'));
      }
    },
    // Re-pull questions + fields and re-point the current selection so the
    // right-side panes (mapped fields / entities) reflect server changes.
    async refreshData() {
      await Promise.all([
        this.reloadQuestions(),
        this.reloadFields(),
        this.reloadGroups(),
        this.$store.dispatch('entities/getEntitiesList'),
      ]);
      if (this.selectedQuestion) {
        this.onSelectQuestion(this.selectedQuestion);
      }
    },
    errorMessage(e, key) {
      const detail = get(e, 'response.data.message', get(e, 'message', ''));
      const base = this.$t(`${this.baseTranslationPath}.${key}`);
      return detail ? `${base} (${detail})` : base;
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.form-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
  margin-bottom: 12px;

  // Match the ManagementPage list title typography + vertical position so "Forms"
  :deep(.middle-column-header) {
    height: auto;
    line-height: 24px;
    font-weight: 400;
  }
}

.button-container {
  display: flex;
  gap: 5px;
  justify-content: flex-end;
  align-items: center;
}

.btn {
  height: 30px;
}

.btn-wrap {
  display: inline-flex;
}

.workspace {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 12px;
  margin-top: 10px;
  background-color: var(--aiq-tab-content-background);
}

.left-pane {
  flex: 0 0 420px;
  min-width: 0;
  border: 1px solid $aiq-tab-content-border;
  border-radius: 4px;
  overflow: hidden;
}

.right-pane {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.right-tabs {
  height: 100%;
  min-height: 0;

  :deep(.el-tabs__content) {
    height: calc(100% - 40px);
    padding: 0;
    background-color: var(--aiq-bgc-middle-column-content);
  }

  :deep(.el-tab-pane) {
    height: 100%;
  }
}
</style>

<style lang="scss">
.forms-kebab-dropdown .el-dropdown-menu {
  overflow-x: hidden;
}

.el-dropdown-menu__item.dropdown_button {
  height: 25px;
  line-height: 25px;
}

.el-dropdown-menu__item.delete-form-item {
  color: #f56c6c;
  background-color: #fff;

  &:hover,
  &:focus {
    color: #f56c6c;
    background-color: #fff;
  }
}
</style>
